import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import CashflowApplyModal from './CashflowApplyModal';
import CashflowApplyHistory from './CashflowApplyHistory';
import CashflowWhatIfChat from './CashflowWhatIfChat';
import {
  Wallet,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Activity,
  Settings as SettingsIcon,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';

/**
 * Dashboard "Cashflow Game Plan" card.
 *
 * Auto-loads the saved scenario on mount (or the sample if none saved) and
 * renders the deterministic plan + AI narration from `POST /api/cashflow/game-plan`.
 * "Edit Cashflow Inputs" jumps to /cashflow/settings. "Demo · using sample"
 * banner is shown only when the backend reports `usingSample: true`.
 */

const STATUS_STYLES = {
  safe: {
    pill: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    dot: 'bg-emerald-400',
    accent: 'from-emerald-900/30 to-slate-900/40 border-emerald-900/40',
  },
  tight: {
    pill: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    dot: 'bg-amber-400',
    accent: 'from-amber-900/30 to-slate-900/40 border-amber-900/40',
  },
  danger: {
    pill: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
    dot: 'bg-rose-400',
    accent: 'from-rose-900/30 to-slate-900/40 border-rose-900/40',
  },
  // Neutral "not decision-ready" styles for starter / demo / unverified /
  // stale modes — deliberately muted so users don't read false confidence
  // from a green Safe or red Danger pill when their inputs are projections
  // or haven't been confirmed recently.
  starter: {
    pill: 'bg-slate-600/30 text-slate-200 border-slate-500/40',
    dot: 'bg-slate-300',
    accent: 'from-slate-800/40 to-slate-900/40 border-slate-700/60',
  },
  demo: {
    pill: 'bg-slate-600/30 text-slate-200 border-slate-500/40',
    dot: 'bg-slate-300',
    accent: 'from-slate-800/40 to-slate-900/40 border-slate-700/60',
  },
  needs_verification: {
    pill: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    dot: 'bg-amber-400',
    accent: 'from-amber-900/30 to-slate-900/40 border-amber-900/40',
  },
  stale: {
    pill: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    dot: 'bg-amber-400',
    accent: 'from-amber-900/30 to-slate-900/40 border-amber-900/40',
  },
};

const fmt = (v) =>
  `$${Number(v || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function MetricBox({ label, value, testId }) {
  return (
    <div
      className="rounded-xl border border-slate-700/60 bg-slate-900/70 p-3"
      data-testid={testId}
    >
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-lg font-semibold text-white mt-0.5">{value}</p>
    </div>
  );
}

function CashflowGamePlanCard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [applyOpen, setApplyOpen] = useState(false);

  const generatePlan = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/cashflow/game-plan`, {});
      setData(res.data);
    } catch (err) {
      console.error('Cashflow game plan failed:', err);
      setError('Failed to generate cashflow plan.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-load plan on mount so the card reflects saved scenario (or sample fallback).
  useEffect(() => {
    generatePlan();
  }, [generatePlan]);

  const plan = data?.plan;
  const advice = data?.aiAdvice;
  const usingSample = !!data?.usingSample;
  const usingStarterTemplate = !!data?.usingStarterTemplate;
  const verification = data?.verification || null;

  // Decision-readiness gate: only treat the deterministic Safe/Tight/Danger
  // status as authoritative when the user has saved AND verified their
  // balances within the freshness window. Starter / demo / unverified /
  // stale all force a muted neutral pill so users don't read false
  // confidence from a green Safe when their underlying data isn't trusted.
  const verificationState = verification?.state;
  const decisionReady = verification?.decisionReady === true;
  let displayStatus;
  let displayStatusLabel;
  if (verificationState && verificationState !== 'verified') {
    displayStatus = STATUS_STYLES[verificationState] || STATUS_STYLES.tight;
    displayStatusLabel = verification?.label || verificationState;
  } else {
    displayStatus = (plan?.status && STATUS_STYLES[plan.status]) || STATUS_STYLES.tight;
    displayStatusLabel = plan?.status || '';
  }

  return (
    <details
      className={`group bg-slate-950 bg-gradient-to-br ${displayStatus.accent} rounded-2xl shadow-xl border overflow-hidden`}
      data-testid="cashflow-game-plan"
      data-decision-ready={decisionReady ? 'true' : 'false'}
    >
      <summary className="cursor-pointer px-6 py-5 select-none flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Wallet className="w-5 h-5 text-white/80" />
            <h2 className="text-lg font-semibold text-white">Cashflow Game Plan</h2>
            <ChevronDown className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-180" />
          </div>
          <p className="text-sm text-gray-400 max-w-xl">
            {decisionReady
              ? 'Bills, savings floors, checking cushions, and debt payoff order. Click to expand.'
              : 'Draft plan from projected balances — not yet decision-ready. Click to expand.'}
          </p>
        </div>

        {plan?.status && (
          <div
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-bold uppercase tracking-wide ${displayStatus.pill}`}
            data-testid="cashflow-status-pill"
          >
            <span className={`inline-block w-2 h-2 rounded-full ${displayStatus.dot}`} />
            <span data-testid="cashflow-status-label">{displayStatusLabel}</span>
          </div>
        )}
      </summary>

      <div className="px-6 pb-6">
        {loading && !data && (
          <div className="h-40 flex items-center justify-center text-gray-400 text-sm">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Computing deterministic plan + CFO advice...
          </div>
        )}

        {error && !plan && (
          <div className="rounded-xl bg-rose-500/10 border border-rose-500/30 px-4 py-3 text-rose-200 text-sm flex items-center justify-between gap-3">
            <span>{error}</span>
            <button
              onClick={generatePlan}
              className="px-3 py-1.5 rounded-md bg-white text-slate-900 text-xs font-semibold hover:bg-gray-100"
              data-testid="cashflow-retry-btn"
            >
              Retry
            </button>
          </div>
        )}

        {plan && (
          <div className="space-y-4">
            {verificationState === 'needs_verification' && (
              <div className="rounded-md bg-amber-500/10 border border-amber-500/40 px-3 py-3 text-xs text-amber-100 flex items-start gap-2" data-testid="cashflow-needs-verification-banner">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-300" />
                <div className="flex-1 space-y-2">
                  <p>
                    <strong className="text-amber-50">Saved scenario — balances not recently verified.</strong>{' '}
                    Open <em>Cashflow Settings</em> and confirm your balances are accurate today to make this plan decision-ready.
                  </p>
                  <button
                    onClick={() => navigate('/cashflow/settings')}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-400 text-slate-900 text-xs font-semibold"
                    data-testid="cashflow-go-verify-btn"
                  >
                    <SettingsIcon className="w-3.5 h-3.5" />
                    Confirm balances
                  </button>
                </div>
              </div>
            )}

            {verificationState === 'stale' && (
              <div className="rounded-md bg-amber-500/10 border border-amber-500/40 px-3 py-3 text-xs text-amber-100 flex items-start gap-2" data-testid="cashflow-stale-banner">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-300" />
                <div className="flex-1 space-y-2">
                  <p>
                    <strong className="text-amber-50">{verification?.helper || 'Re-verify balances.'}</strong>{' '}
                    Confirm in Cashflow Settings that your balances still match your bank app to keep this plan decision-ready.
                  </p>
                  <button
                    onClick={() => navigate('/cashflow/settings')}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-400 text-slate-900 text-xs font-semibold"
                    data-testid="cashflow-go-reverify-btn"
                  >
                    <SettingsIcon className="w-3.5 h-3.5" />
                    Re-verify balances
                  </button>
                </div>
              </div>
            )}

            {verificationState === 'verified' && (
              <p
                className="text-xs text-emerald-300/90 flex items-center gap-1.5"
                data-testid="cashflow-verified-line"
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400" />
                {verification?.helper || 'Verified.'}
              </p>
            )}

            {usingStarterTemplate && (
              <div className="rounded-md bg-amber-500/10 border border-amber-500/40 px-3 py-3 text-xs text-amber-100 flex items-start gap-2" data-testid="cashflow-starter-banner">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-300" />
                <div className="flex-1 space-y-2">
                  <p>
                    <strong className="text-amber-50">This plan is using projected starter balances.</strong>{' '}
                    Update and save your real balances before relying on these recommendations. The underlying numbers come from your PaperTrail projections — not your bank app.
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => navigate('/cashflow/settings')}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-400 text-slate-900 text-xs font-semibold"
                      data-testid="cashflow-verify-balances-btn"
                    >
                      <SettingsIcon className="w-3.5 h-3.5" />
                      Verify balances → Edit Cashflow Inputs
                    </button>
                    <span className="text-[11px] text-amber-200/80">
                      Enter the balances from your bank app, add upcoming bills, then save.
                    </span>
                  </div>
                </div>
              </div>
            )}

            {usingSample && (
              <div className="rounded-md bg-amber-500/10 border border-amber-500/40 px-3 py-3 text-xs text-amber-100 flex items-start gap-2" data-testid="cashflow-sample-banner">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-300" />
                <div className="flex-1 space-y-2">
                  <p>
                    <strong className="text-amber-50">Demo mode — built-in sample scenario.</strong>{' '}
                    This plan is for illustration only. Set up your Financial Plan, then your Cashflow Game Plan will use your own accounts.
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => navigate('/cashflow/settings')}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-400 text-slate-900 text-xs font-semibold"
                      data-testid="cashflow-verify-balances-btn-sample"
                    >
                      <SettingsIcon className="w-3.5 h-3.5" />
                      Edit Cashflow Inputs
                    </button>
                    <span className="text-[11px] text-amber-200/80">
                      Enter your accounts, balances, and bills, then save.
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="rounded-xl bg-slate-900/70 border border-slate-700/60 p-4">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Activity className="w-4 h-4 text-gray-300" />
                <p className="text-sm font-semibold text-white">AI Summary</p>
                {!decisionReady && (
                  <span
                    className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-slate-700/70 text-slate-200 border border-slate-600/70"
                    data-testid="cashflow-ai-summary-draft-badge"
                    title="This summary is based on projected starter balances, not verified bank data"
                  >
                    Draft
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-200 leading-relaxed" data-testid="cashflow-ai-summary">
                {advice?.executiveSummary || plan.summary}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <MetricBox
                label="Upcoming Required Bills"
                value={fmt(plan.metrics.totalRequiredBills)}
                testId="cashflow-metric-bills"
              />
              <MetricBox
                label="Projected Checking"
                value={fmt(plan.metrics.projectedCheckingAfterBills)}
                testId="cashflow-metric-projected"
              />
              <MetricBox
                label="HYSA Gap"
                value={fmt(plan.metrics.hysaGap)}
                testId="cashflow-metric-hysa-gap"
              />
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <p className="text-sm font-semibold text-white">Next Moves</p>
                {!decisionReady && (
                  <span
                    className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-slate-700/70 text-slate-200 border border-slate-600/70"
                    data-testid="cashflow-actions-draft-badge"
                  >
                    Draft
                  </span>
                )}
              </div>
              <div className="space-y-2" data-testid="cashflow-actions">
                {plan.actions.map((a) => (
                  <div
                    key={a.step}
                    className="flex items-start gap-3 rounded-xl border border-slate-700/60 bg-slate-900/70 p-3"
                  >
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-800 text-xs font-bold text-gray-200 shrink-0">
                      {a.step}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white">
                        {a.label}
                        {a.amount > 0 && (
                          <span className="text-gray-400 font-normal"> — {fmt(a.amount)}</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-400 leading-snug mt-0.5">{a.reason}</p>
                      <span className="inline-block mt-1 text-[10px] uppercase tracking-wide text-gray-500">
                        {a.timing.replace(/_/g, ' ')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {advice?.doNotDoYet?.length > 0 && (
              <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4" data-testid="cashflow-do-not-do">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-rose-300" />
                  <p className="text-sm font-semibold text-rose-200">Do Not Do Yet</p>
                </div>
                <ul className="list-disc pl-5 space-y-1 text-sm text-rose-100">
                  {advice.doNotDoYet.map((item, idx) => (
                    <li key={`${idx}-${item.slice(0, 16)}`}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {(plan.warnings?.length || advice?.risks?.length) ? (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4" data-testid="cashflow-risks">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-amber-300" />
                  <p className="text-sm font-semibold text-amber-200">Risks &amp; Warnings</p>
                </div>
                <ul className="list-disc pl-5 space-y-1 text-sm text-amber-100">
                  {[...(plan.warnings || []), ...(advice?.risks || [])].map((item, idx) => (
                    <li key={`${idx}-${item.slice(0, 16)}`}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {advice?.finalRecommendation && (
              <div className="rounded-xl border border-slate-700/60 bg-slate-900/70 p-4 flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" />
                <p className="text-sm text-gray-200" data-testid="cashflow-final-recommendation">
                  <strong className="text-white">Final recommendation: </strong>
                  {advice.finalRecommendation}
                </p>
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => navigate('/cashflow/settings')}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white text-slate-900 text-xs font-semibold hover:bg-gray-100"
                  data-testid="cashflow-edit-inputs-btn"
                >
                  <SettingsIcon className="w-3.5 h-3.5" />
                  Edit Cashflow Inputs
                </button>
                {decisionReady && (
                  <button
                    onClick={() => setApplyOpen(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-slate-900 text-xs font-semibold"
                    data-testid="cashflow-apply-moves-btn"
                    title="Apply selected planner moves to your actual balances"
                  >
                    <ShieldCheck className="w-3.5 h-3.5" />
                    Apply these moves
                  </button>
                )}
              </div>
              <button
                onClick={generatePlan}
                disabled={loading}
                className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-white underline underline-offset-2 disabled:opacity-60"
                data-testid="cashflow-regenerate-btn"
              >
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Regenerate plan
              </button>
            </div>

            <CashflowApplyHistory />
            <CashflowWhatIfChat />
          </div>
        )}
      </div>

      {applyOpen && data?.plan && (
        <CashflowApplyModal
          plan={data.plan}
          scenario={data.input}
          onClose={() => setApplyOpen(false)}
          onApplied={() => generatePlan()}
        />
      )}
    </details>
  );
}

export default CashflowGamePlanCard;
